import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  ChevronLeft, 
  ChevronRight, 
  Compass, 
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Clock,
  FileText,
  Mail,
  MessageCircle,
  Image as ImageIcon,
  Lightbulb,
  Target,
  TrendingUp,
  Users,
  Zap,
  GraduationCap,
  Briefcase,
  ShoppingBag,
  Monitor,
  MapPin,
  Heart,
  Palette,
  Code,
  Leaf,
  Home,
  Wrench,
  DollarSign,
  Calendar,
  Megaphone,
  Store,
  Utensils,
  Scissors,
  BookOpen,
  Settings,
  Loader2,
  Plus,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import { FUNNEL_STRATEGIES, getStrategyById, getVariantById } from '@/config/funnelStrategies';

// ==================== PRODUCT TYPES ====================
const PRODUCT_TYPES = {
  conocimiento: { 
    id: 'conocimiento', 
    label: 'Conocimiento / Formación', 
    icon: GraduationCap, 
    emoji: '🎓',
    description: 'Cursos, mentorías, membresías, formaciones'
  },
  consultoria: { 
    id: 'consultoria', 
    label: 'Mi tiempo / Consultoría', 
    icon: Briefcase, 
    emoji: '💼',
    description: 'Servicios de consultoría, coaching, asesoría'
  },
  fisico: { 
    id: 'fisico', 
    label: 'Productos físicos', 
    icon: ShoppingBag, 
    emoji: '🛍️',
    description: 'Ecommerce, productos tangibles'
  },
  software: { 
    id: 'software', 
    label: 'Software / App', 
    icon: Monitor, 
    emoji: '💻',
    description: 'SaaS, aplicaciones, herramientas digitales'
  },
  local: { 
    id: 'local', 
    label: 'Negocio local', 
    icon: MapPin, 
    emoji: '🏪',
    description: 'Servicios presenciales, comercios locales'
  }
};

// ==================== NICHES ====================
const NICHES = {
  conocimiento: [
    { id: 'salud', label: 'Salud y bienestar', icon: Heart, emoji: '💪', 
      subniches: ['Fitness', 'Nutrición', 'Yoga', 'Psicología', 'Meditación', 'Otros'] },
    { id: 'negocios', label: 'Negocios y marketing', icon: Briefcase, emoji: '💼',
      subniches: ['Marketing digital', 'Ventas', 'Productividad', 'Finanzas', 'RRHH', 'Emprendimiento', 'Otros'] },
    { id: 'creatividad', label: 'Creatividad y arte', icon: Palette, emoji: '🎨',
      subniches: ['Diseño', 'Fotografía', 'Música', 'Escritura', 'Vídeo', 'Otros'] },
    { id: 'tecnologia', label: 'Tecnología', icon: Code, emoji: '💻',
      subniches: ['Programación', 'IA', 'Ciberseguridad', 'No-code', 'Otros'] },
    { id: 'desarrollo', label: 'Desarrollo personal', icon: Leaf, emoji: '🌱',
      subniches: ['Mindset', 'Hábitos', 'Relaciones', 'Liderazgo', 'Otros'] },
    { id: 'lifestyle', label: 'Lifestyle', icon: Home, emoji: '🏠',
      subniches: ['Cocina', 'Viajes', 'Moda', 'Decoración', 'Otros'] },
    { id: 'otro', label: 'Otro nicho', icon: Wrench, emoji: '🔧', isCustom: true }
  ],
  consultoria: [
    { id: 'marketing', label: 'Marketing y publicidad', icon: Megaphone },
    { id: 'negocio', label: 'Consultoría de negocio', icon: Briefcase },
    { id: 'legal', label: 'Legal / Finanzas', icon: FileText },
    { id: 'salud', label: 'Salud (médico, psicólogo, nutricionista)', icon: Heart },
    { id: 'tech', label: 'Tecnología / IT', icon: Code },
    { id: 'coaching', label: 'Coaching personal', icon: Users },
    { id: 'otro', label: 'Otro', icon: Wrench, isCustom: true }
  ],
  fisico: [
    { id: 'moda', label: 'Moda y accesorios', icon: ShoppingBag },
    { id: 'belleza', label: 'Salud y belleza', icon: Heart },
    { id: 'hogar', label: 'Hogar y decoración', icon: Home },
    { id: 'alimentacion', label: 'Alimentación', icon: Utensils },
    { id: 'deporte', label: 'Deporte', icon: Zap },
    { id: 'tech', label: 'Tecnología / Gadgets', icon: Monitor },
    { id: 'otro', label: 'Otro', icon: Wrench, isCustom: true }
  ],
  local: [
    { id: 'salud', label: 'Salud (clínica, fisio, médico)', icon: Heart },
    { id: 'bienestar', label: 'Bienestar (gym, yoga, spa)', icon: Leaf },
    { id: 'alimentacion', label: 'Alimentación (restaurante, catering)', icon: Utensils },
    { id: 'belleza', label: 'Belleza (peluquería, estética)', icon: Scissors },
    { id: 'educacion', label: 'Educación (academia, clases)', icon: BookOpen },
    { id: 'servicios', label: 'Servicios profesionales', icon: Briefcase },
    { id: 'otro', label: 'Otro', icon: Wrench, isCustom: true }
  ]
};

// ==================== DYNAMIC QUESTIONS BUILDER ====================
const buildQuestions = (productType, answers) => {
  const questions = [];
  
  // P1 siempre es el tipo de producto (ya respondido)
  
  switch (productType) {
    case 'conocimiento':
      // P2: ¿Cómo lo vendes?
      questions.push({
        id: 'modelo_venta',
        question: '¿Cómo vendes tu conocimiento?',
        key: 'modelo_venta',
        options: [
          { id: 'puntual', label: 'Una vez (curso, mentoría, formación)', icon: FileText },
          { id: 'recurrente', label: 'Cada mes (membresía, comunidad, suscripción)', icon: Calendar }
        ]
      });
      
      // P3: ¿En qué nicho?
      questions.push({
        id: 'nicho',
        question: '¿En qué nicho te especializas?',
        key: 'nicho',
        options: NICHES.conocimiento.map(n => ({
          id: n.id,
          label: n.label,
          icon: n.icon,
          emoji: n.emoji,
          subniches: n.subniches,
          isCustom: n.isCustom
        }))
      });
      
      // P4: ¿Cuánto cuesta?
      questions.push({
        id: 'precio',
        question: '¿Cuánto cuesta tu producto principal?',
        key: 'precio',
        options: [
          { id: 'menos_100', label: 'Menos de 100€', icon: DollarSign },
          { id: '100_500', label: '100€ - 500€', icon: DollarSign },
          { id: '500_2000', label: '500€ - 2.000€', icon: DollarSign },
          { id: 'mas_2000', label: 'Más de 2.000€', icon: DollarSign }
        ]
      });
      
      // P5: ¿Tienes audiencia?
      questions.push({
        id: 'audiencia',
        question: '¿Tienes audiencia propia?',
        key: 'audiencia',
        options: [
          { id: 'lista_grande', label: 'Lista de email +500 contactos', icon: Mail },
          { id: 'redes_grande', label: 'Redes +1.000 seguidores', icon: Users },
          { id: 'algo_pequeno', label: 'Algo pequeño (menos de 500)', icon: Users },
          { id: 'nada', label: 'Nada todavía', icon: Users },
          { id: 'clientes_sin_lista', label: 'Clientes pero sin lista', icon: Users }
        ]
      });
      
      // P6: ¿Has pagado publicidad?
      questions.push({
        id: 'publicidad',
        question: '¿Cuál es tu experiencia con publicidad?',
        key: 'publicidad',
        options: [
          { id: 'mas_500', label: 'Sí, invierto +500€/mes', icon: Megaphone },
          { id: '100_500', label: 'Sí, invierto 100-500€/mes', icon: Megaphone },
          { id: 'organico', label: 'Solo contenido orgánico', icon: MessageCircle },
          { id: 'combinado', label: 'Combino ambos', icon: Zap },
          { id: 'nunca', label: 'Nunca he hecho ads', icon: Lightbulb }
        ]
      });
      
      // P7: ¿Cuánto tiempo tienes?
      questions.push({
        id: 'tiempo',
        question: '¿Cuánto tiempo tienes para lanzar?',
        key: 'tiempo',
        options: [
          { id: 'menos_2_semanas', label: 'Menos de 2 semanas', icon: Clock, highlight: true },
          { id: '1_mes', label: '1 mes', icon: Clock },
          { id: '2_3_meses', label: '2-3 meses', icon: Clock },
          { id: 'mas_3_meses', label: 'Más de 3 meses', icon: Clock }
        ]
      });
      
      // P8: ¿Qué quieres conseguir?
      questions.push({
        id: 'objetivo',
        question: '¿Qué quieres conseguir principalmente?',
        key: 'objetivo',
        options: [
          { id: 'ingresos_rapidos', label: 'Ingresos rápidos', icon: Zap },
          { id: 'comunidad', label: 'Comunidad a largo plazo', icon: Users },
          { id: 'recurrencia', label: 'Ingresos recurrentes', icon: TrendingUp },
          { id: 'pocos_alto_valor', label: 'Pocos clientes de alto valor', icon: Target },
          { id: 'validar', label: 'Validar la idea', icon: Lightbulb }
        ]
      });
      
      // P9: ¿Experiencia vendiendo online?
      questions.push({
        id: 'experiencia',
        question: '¿Cuánta experiencia tienes vendiendo online?',
        key: 'experiencia',
        options: [
          { id: 'nunca', label: 'Nunca he vendido', icon: Lightbulb },
          { id: 'sin_exito', label: 'He intentado sin éxito', icon: Target },
          { id: 'resultados', label: 'Tengo algunos resultados', icon: TrendingUp },
          { id: 'regularmente', label: 'Lanzo regularmente', icon: Sparkles }
        ]
      });
      break;
      
    case 'consultoria':
      // P2: ¿En qué nicho?
      questions.push({
        id: 'nicho',
        question: '¿En qué área ofreces tus servicios?',
        key: 'nicho',
        options: NICHES.consultoria.map(n => ({
          id: n.id,
          label: n.label,
          icon: n.icon,
          isCustom: n.isCustom
        }))
      });
      
      // P3: ¿Cuánto cobras?
      questions.push({
        id: 'precio',
        question: '¿Cuánto cobras por cliente?',
        key: 'precio',
        options: [
          { id: 'menos_500', label: 'Menos de 500€', icon: DollarSign },
          { id: '500_2000', label: '500€ - 2.000€', icon: DollarSign },
          { id: '2000_5000', label: '2.000€ - 5.000€', icon: DollarSign },
          { id: 'mas_5000', label: 'Más de 5.000€', icon: DollarSign }
        ]
      });
      
      // P4: ¿Cómo consigues clientes?
      questions.push({
        id: 'captacion',
        question: '¿Cómo consigues clientes ahora?',
        key: 'captacion',
        options: [
          { id: 'referencias', label: 'Referencias / boca a boca', icon: Users },
          { id: 'redes', label: 'Redes sociales', icon: MessageCircle },
          { id: 'publicidad', label: 'Publicidad', icon: Megaphone },
          { id: 'sin_sistema', label: 'No tengo sistema', icon: Lightbulb }
        ]
      });
      
      // P5: ¿Cuántos clientes quieres?
      questions.push({
        id: 'volumen',
        question: '¿Cuántos clientes quieres al mes?',
        key: 'volumen',
        options: [
          { id: '1_3', label: '1-3 clientes exclusivos', icon: Target },
          { id: '5_10', label: '5-10 clientes', icon: Users },
          { id: 'maximo', label: 'Los máximos posibles', icon: TrendingUp }
        ]
      });
      
      // P6: ¿Tienes audiencia online?
      questions.push({
        id: 'audiencia',
        question: '¿Tienes presencia online?',
        key: 'audiencia',
        options: [
          { id: 'grande', label: 'Sí, +1.000 seguidores', icon: Users },
          { id: 'pequena', label: 'Algo pequeño (-1.000)', icon: Users },
          { id: 'nada', label: 'Casi nada online', icon: Lightbulb }
        ]
      });
      
      // P7: ¿Qué formato prefieres?
      questions.push({
        id: 'formato',
        question: '¿Qué formato de venta prefieres?',
        key: 'formato',
        options: [
          { id: 'llamada', label: 'Llamada 1 a 1 directamente', icon: MessageCircle },
          { id: 'webinar', label: 'Webinar/Masterclass antes', icon: Users },
          { id: 'challenge', label: 'Challenge/Reto de varios días', icon: Zap },
          { id: 'automatizado', label: 'Lo más automatizado posible', icon: Settings }
        ]
      });
      break;
      
    case 'fisico':
      // P2: ¿En qué categoría?
      questions.push({
        id: 'categoria',
        question: '¿En qué categoría está tu producto?',
        key: 'categoria',
        options: NICHES.fisico.map(n => ({
          id: n.id,
          label: n.label,
          icon: n.icon,
          isCustom: n.isCustom
        }))
      });
      
      // P3: ¿Tienes tienda online?
      questions.push({
        id: 'tienda',
        question: '¿Tienes tienda online ya?',
        key: 'tienda',
        options: [
          { id: 'con_ventas', label: 'Sí, con ventas regulares', icon: TrendingUp },
          { id: 'pocas_ventas', label: 'Sí, pero pocas ventas', icon: Store },
          { id: 'no_tengo', label: 'No, quiero crearla', icon: Lightbulb }
        ]
      });
      
      // P4: ¿Precio del producto estrella?
      questions.push({
        id: 'precio',
        question: '¿Cuánto cuesta tu producto estrella?',
        key: 'precio',
        options: [
          { id: 'menos_30', label: 'Menos de 30€', icon: DollarSign },
          { id: '30_100', label: '30€ - 100€', icon: DollarSign },
          { id: '100_300', label: '100€ - 300€', icon: DollarSign },
          { id: 'mas_300', label: 'Más de 300€', icon: DollarSign }
        ]
      });
      
      // P5: ¿Stock o bajo demanda?
      questions.push({
        id: 'stock',
        question: '¿Tienes stock o es bajo demanda?',
        key: 'stock',
        options: [
          { id: 'stock', label: 'Stock disponible ahora', icon: ShoppingBag },
          { id: 'demanda', label: 'Producción bajo demanda', icon: Settings },
          { id: 'dropshipping', label: 'Dropshipping', icon: TrendingUp }
        ]
      });
      
      // P6: ¿Cuál es tu objetivo principal?
      questions.push({
        id: 'objetivo',
        question: '¿Cuál es tu objetivo principal ahora?',
        key: 'objetivo',
        options: [
          { id: 'ventas_rapidas', label: 'Generar ventas rápidas', icon: Zap },
          { id: 'lanzar_coleccion', label: 'Lanzar nueva colección', icon: Sparkles },
          { id: 'recuperar_carritos', label: 'Recuperar carritos abandonados', icon: ShoppingBag },
          { id: 'fidelizar', label: 'Fidelizar clientes existentes', icon: Heart }
        ]
      });
      
      // P7: ¿Qué canal usas para vender?
      questions.push({
        id: 'canal',
        question: '¿Por qué canal vendes más?',
        key: 'canal',
        options: [
          { id: 'web_propia', label: 'Web propia (Shopify, WooCommerce...)', icon: Store },
          { id: 'marketplace', label: 'Marketplaces (Amazon, Etsy...)', icon: ShoppingBag },
          { id: 'redes', label: 'Redes sociales directamente', icon: MessageCircle },
          { id: 'fisico', label: 'Tienda física principalmente', icon: MapPin }
        ]
      });
      break;
      
    case 'software':
      // P2: ¿Cómo se paga?
      questions.push({
        id: 'modelo',
        question: '¿Cómo se paga tu software?',
        key: 'modelo',
        options: [
          { id: 'suscripcion', label: 'Suscripción mensual/anual', icon: Calendar },
          { id: 'unico', label: 'Pago único (licencia)', icon: DollarSign },
          { id: 'uso', label: 'Por uso / créditos', icon: Zap }
        ]
      });
      
      // P3: ¿A quién va dirigido?
      questions.push({
        id: 'target',
        question: '¿A quién va dirigido?',
        key: 'target',
        options: [
          { id: 'b2c', label: 'Particulares (B2C)', icon: Users },
          { id: 'smb', label: 'Pequeñas empresas (SMB)', icon: Store },
          { id: 'enterprise', label: 'Empresas grandes (Enterprise)', icon: Briefcase }
        ]
      });
      
      // P4: ¿En qué fase está?
      questions.push({
        id: 'fase',
        question: '¿En qué fase está tu producto?',
        key: 'fase',
        options: [
          { id: 'construyendo', label: 'Aún lo estoy construyendo', icon: Settings },
          { id: 'beta', label: 'Tengo una versión beta', icon: Code },
          { id: 'lanzado', label: 'Ya está lanzado con usuarios', icon: Users },
          { id: 'escalar', label: 'Quiero escalar lo que funciona', icon: TrendingUp }
        ]
      });
      
      // P5: ¿Cuánto cuesta al mes?
      questions.push({
        id: 'precio',
        question: '¿Cuánto cuesta al mes?',
        key: 'precio',
        options: [
          { id: 'freemium', label: 'Gratis con plan de pago', icon: Lightbulb },
          { id: 'menos_50', label: 'Menos de 50€/mes', icon: DollarSign },
          { id: '50_200', label: '50€ - 200€/mes', icon: DollarSign },
          { id: 'mas_200', label: 'Más de 200€/mes', icon: DollarSign }
        ]
      });
      
      // P6: ¿Cómo adquieres usuarios?
      questions.push({
        id: 'adquisicion',
        question: '¿Cómo adquieres usuarios actualmente?',
        key: 'adquisicion',
        options: [
          { id: 'organico', label: 'SEO / contenido orgánico', icon: Leaf },
          { id: 'ads', label: 'Publicidad (Google, Meta...)', icon: Megaphone },
          { id: 'partnerships', label: 'Partners / integraciones', icon: Users },
          { id: 'nada', label: 'No tengo estrategia clara', icon: Lightbulb }
        ]
      });
      
      // P7: ¿Tu mayor reto ahora?
      questions.push({
        id: 'reto',
        question: '¿Cuál es tu mayor reto ahora?',
        key: 'reto',
        options: [
          { id: 'primeros_usuarios', label: 'Conseguir los primeros usuarios', icon: Users },
          { id: 'activacion', label: 'Que los usuarios usen el producto', icon: Zap },
          { id: 'conversion', label: 'Convertir free a pago', icon: DollarSign },
          { id: 'churn', label: 'Reducir cancelaciones', icon: TrendingUp }
        ]
      });
      break;
      
    case 'local':
      // P2: ¿Tipo de negocio?
      questions.push({
        id: 'tipo',
        question: '¿Qué tipo de negocio tienes?',
        key: 'tipo_negocio',
        options: NICHES.local.map(n => ({
          id: n.id,
          label: n.label,
          icon: n.icon,
          isCustom: n.isCustom
        }))
      });
      
      // P3: ¿Qué quieres conseguir?
      questions.push({
        id: 'objetivo',
        question: '¿Qué quieres conseguir?',
        key: 'objetivo',
        options: [
          { id: 'nuevos', label: 'Más clientes nuevos', icon: Users },
          { id: 'recuperar', label: 'Recuperar clientes antiguos', icon: Target },
          { id: 'agenda', label: 'Llenar agenda / reservas', icon: Calendar },
          { id: 'fidelizar', label: 'Fidelizar y repetir visitas', icon: Heart }
        ]
      });
      
      // P4: ¿Cómo te encuentran?
      questions.push({
        id: 'descubrimiento',
        question: '¿Cómo te encuentran ahora?',
        key: 'descubrimiento',
        options: [
          { id: 'google', label: 'Google / Mapas', icon: MapPin },
          { id: 'redes', label: 'Redes sociales', icon: MessageCircle },
          { id: 'boca_boca', label: 'Boca a boca', icon: Users },
          { id: 'mal', label: 'No me encuentran bien', icon: Lightbulb }
        ]
      });
      
      // P5: ¿Ticket medio?
      questions.push({
        id: 'ticket',
        question: '¿Cuánto gasta un cliente de media?',
        key: 'ticket',
        options: [
          { id: 'menos_30', label: 'Menos de 30€', icon: DollarSign },
          { id: '30_100', label: '30€ - 100€', icon: DollarSign },
          { id: '100_300', label: '100€ - 300€', icon: DollarSign },
          { id: 'mas_300', label: 'Más de 300€', icon: DollarSign }
        ]
      });
      
      // P6: ¿Tienes base de datos de clientes?
      questions.push({
        id: 'database',
        question: '¿Tienes base de datos de clientes?',
        key: 'database',
        options: [
          { id: 'grande', label: 'Sí, +500 contactos', icon: Mail },
          { id: 'pequena', label: 'Sí, menos de 500', icon: Mail },
          { id: 'no_tengo', label: 'No tengo lista organizada', icon: Lightbulb }
        ]
      });
      
      // P7: ¿Qué presupuesto tienes para publicidad?
      questions.push({
        id: 'presupuesto',
        question: '¿Cuánto puedes invertir en publicidad al mes?',
        key: 'presupuesto',
        options: [
          { id: 'menos_200', label: 'Menos de 200€/mes', icon: DollarSign },
          { id: '200_500', label: '200€ - 500€/mes', icon: DollarSign },
          { id: 'mas_500', label: 'Más de 500€/mes', icon: DollarSign },
          { id: 'nada', label: 'Solo orgánico por ahora', icon: Leaf }
        ]
      });
      break;
  }
  
  return questions;
};

// ==================== RECOMMENDATION ALGORITHM ====================
const calculateRecommendation = (productType, answers) => {
  let strategyId = 'lanzamiento';
  let variantId = '1-directo';
  let explanation = '';
  
  switch (productType) {
    case 'conocimiento':
      const isRecurrente = answers.modelo_venta === 'recurrente';
      const precio = answers.precio;
      const audiencia = answers.audiencia;
      const tiempo = answers.tiempo;
      const objetivo = answers.objetivo;
      const experiencia = answers.experiencia;
      
      if (isRecurrente) {
        // MEMBRESÍA
        strategyId = 'membresia';
        if (precio === 'menos_100') {
          variantId = 'prueba-gratuita';
          explanation = 'Una membresía de precio bajo funciona mejor con una prueba gratuita para reducir la barrera de entrada.';
        } else if (precio === '100_500' && (audiencia === 'lista_grande' || audiencia === 'redes_grande')) {
          variantId = 'waitlist-apertura';
          explanation = 'Con audiencia y precio medio, una waitlist con aperturas genera escasez y urgencia.';
        } else {
          variantId = 'founding-members';
          explanation = 'Para membresías premium, empezar con Founding Members crea exclusividad.';
        }
      } else {
        // CONOCIMIENTO PUNTUAL
        if (precio === 'mas_2000') {
          // HIGH TICKET
          strategyId = 'high-ticket';
          variantId = 'aplicacion-llamada';
          explanation = 'Con tickets altos (+2.000€), necesitas un proceso de cualificación con formulario + llamada.';
        } else if (objetivo === 'validar') {
          // VALIDACIÓN
          strategyId = 'organico';
          variantId = 'lanzamiento-semilla';
          explanation = 'Para validar tu idea, un lanzamiento semilla orgánico te da feedback real sin riesgo.';
        } else if (objetivo === 'comunidad' || objetivo === 'recurrencia') {
          // DESAFÍO
          strategyId = 'desafio';
          variantId = 'reto-5-dias';
          explanation = 'Un reto de 5 días construye comunidad y genera confianza antes de vender.';
        } else if (tiempo === 'menos_2_semanas') {
          // EXPRESS
          strategyId = 'evergreen';
          variantId = 'vsl';
          explanation = 'Con poco tiempo, un VSL evergreen te permite lanzar rápido y escalar.';
        } else if ((audiencia === 'nada' || audiencia === 'algo_pequeno') && precio === 'menos_100') {
          // SIN AUDIENCIA + PRECIO BAJO
          strategyId = 'evergreen';
          variantId = 'tripwire';
          explanation = 'Sin audiencia y precio bajo, un tripwire con ads te permite captar y vender rápido.';
        } else if (audiencia === 'lista_grande' || audiencia === 'redes_grande') {
          // CON AUDIENCIA
          if (tiempo === '2_3_meses' || tiempo === 'mas_3_meses') {
            if (experiencia === 'resultados' || experiencia === 'regularmente') {
              strategyId = 'plf';
              variantId = 'plf-clasico';
              explanation = 'Con audiencia, tiempo y experiencia, un PLF clásico maximiza tus resultados.';
            } else {
              strategyId = 'plf';
              variantId = 'mini-plf';
              explanation = 'Un mini PLF de 3 vídeos te da estructura sin ser abrumador.';
            }
          } else if (tiempo === '1_mes') {
            strategyId = 'lanzamiento';
            variantId = '2-directos';
            explanation = 'Con 1 mes y audiencia, 2 directos (masterclass + cierre) es el equilibrio perfecto.';
          } else {
            strategyId = 'lanzamiento';
            variantId = '1-directo';
            explanation = 'Un único directo de alta conversión con tu audiencia actual.';
          }
        } else {
          // CASO GENERAL
          if (precio === '100_500') {
            strategyId = 'evergreen';
            variantId = 'vsl';
            explanation = 'Un VSL evergreen para este rango de precio te permite escalar sin límites.';
          } else if (precio === '500_2000') {
            strategyId = 'lanzamiento';
            variantId = '2-directos';
            explanation = 'Para programas premium, 2 directos generan la confianza necesaria para el precio.';
          } else {
            strategyId = 'evergreen';
            variantId = 'tripwire';
            explanation = 'Un tripwire automatizado para productos de entrada.';
          }
        }
      }
      break;
      
    case 'consultoria':
      // SIEMPRE HIGH TICKET
      strategyId = 'high-ticket';
      const precioConsultoria = answers.precio;
      const volumen = answers.volumen;
      
      if (precioConsultoria === 'mas_5000' || volumen === '1_3') {
        variantId = 'aplicacion-llamada';
        explanation = 'Para servicios de alto valor y pocos clientes, un VSL + aplicación + llamada filtra perfectamente.';
      } else if (precioConsultoria === '2000_5000') {
        variantId = 'micro-webinar-llamada';
        explanation = 'Un micro-webinar de 20 min calienta leads antes de la llamada de ventas.';
      } else {
        variantId = 'challenge-high-ticket';
        explanation = 'Un challenge de 3-5 días genera confianza y cualifica prospectos antes de vender.';
      }
      break;
      
    case 'fisico':
      // SIEMPRE ECOMMERCE
      strategyId = 'ecommerce';
      const tienda = answers.tienda;
      const stock = answers.stock;
      
      if (tienda === 'con_ventas' && stock === 'stock') {
        variantId = 'flash-sale';
        explanation = 'Con stock y ventas, una flash sale genera liquidez rápida y urgencia.';
      } else if (tienda === 'pocas_ventas') {
        variantId = 'recuperacion-carrito';
        explanation = 'Recuperar carritos abandonados es lo primero que debes optimizar.';
      } else {
        variantId = 'venta-directa';
        explanation = 'Un funnel de venta directa con página de producto optimizada.';
      }
      break;
      
    case 'software':
      // SIEMPRE SAAS
      strategyId = 'saas';
      const fase = answers.fase;
      const target = answers.target;
      const precio_saas = answers.precio;
      
      if (target === 'enterprise') {
        variantId = 'saas-demo';
        explanation = 'Para enterprise, necesitas demos personalizadas con proceso de ventas consultivo.';
      } else if (fase === 'beta' || fase === 'construyendo') {
        variantId = 'saas-free-trial';
        explanation = 'En fase beta, una prueba gratuita te permite iterar con early adopters.';
      } else if (precio_saas === 'freemium') {
        variantId = 'saas-freemium';
        explanation = 'Con modelo freemium, el funnel se centra en activación y conversión a pago.';
      } else {
        variantId = 'saas-free-trial';
        explanation = 'Un trial gratuito de 7-14 días es el estándar para SaaS B2C/SMB.';
      }
      break;
      
    case 'local':
      // SIEMPRE LOCAL
      strategyId = 'local';
      const objetivo_local = answers.objetivo;
      const descubrimiento = answers.descubrimiento;
      
      if (objetivo_local === 'recuperar') {
        variantId = 'reactivacion-db';
        explanation = 'Reactivar clientes inactivos con email/SMS tiene el mejor ROI en negocios locales.';
      } else if (descubrimiento === 'google' || descubrimiento === 'mal') {
        variantId = 'google-local';
        explanation = 'Google Ads con landing local captura búsquedas con intención alta.';
      } else {
        variantId = 'lead-gen-oferta';
        explanation = 'Un funnel de generación de leads con oferta de primera visita.';
      }
      break;
  }
  
  return {
    strategyId,
    variantId,
    explanation,
    alternatives: getAlternatives(productType, answers, strategyId)
  };
};

// Get alternative recommendations
const getAlternatives = (productType, answers, primaryStrategy) => {
  const alternatives = [];
  
  switch (productType) {
    case 'conocimiento':
      if (primaryStrategy !== 'evergreen') {
        alternatives.push({ strategyId: 'evergreen', variantId: 'vsl', reason: 'Alternativa evergreen para escalar sin límites' });
      }
      if (primaryStrategy !== 'desafio' && answers.objetivo === 'comunidad') {
        alternatives.push({ strategyId: 'desafio', variantId: 'reto-5-dias', reason: 'Un reto para construir comunidad' });
      }
      break;
    case 'consultoria':
      if (answers.volumen !== '1_3') {
        alternatives.push({ strategyId: 'desafio', variantId: 'reto-5-dias', reason: 'Un reto para nutrir más leads' });
      }
      break;
    case 'fisico':
      alternatives.push({ strategyId: 'ecommerce', variantId: 'lanzamiento-coleccion', reason: 'Para lanzar nuevas colecciones' });
      break;
    case 'software':
      if (primaryStrategy !== 'saas') {
        alternatives.push({ strategyId: 'saas', variantId: 'saas-free-trial', reason: 'Trial gratuito estándar' });
      }
      break;
  }
  
  return alternatives.slice(0, 2);
};

// ==================== MAIN COMPONENT ====================
const StrategyAdvisor = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  // State
  const [productType, setProductType] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [customNiche, setCustomNiche] = useState('');
  const [selectedSubniche, setSelectedSubniche] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  
  // Client modal state
  const [showClientModal, setShowClientModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [newClientName, setNewClientName] = useState('');
  const [creatingClient, setCreatingClient] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null); // Store strategy/variant for navigation
  
  // Build questions dynamically
  const questions = useMemo(() => {
    if (!productType) return [];
    return buildQuestions(productType, answers);
  }, [productType, answers]);
  
  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;
  const progress = totalQuestions > 0 ? ((currentQuestionIndex + 1) / totalQuestions) * 100 : 0;
  
  // Handle product type selection
  const handleProductTypeSelect = (type) => {
    setProductType(type);
    setAnswers({});
    setCurrentQuestionIndex(0);
  };
  
  // Handle answer selection
  const handleAnswer = (optionId) => {
    const newAnswers = { ...answers, [currentQuestion.key]: optionId };
    setAnswers(newAnswers);
    
    // Auto-advance after short delay
    setTimeout(() => {
      if (currentQuestionIndex < totalQuestions - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        // Calculate and show result
        const result = calculateRecommendation(productType, newAnswers);
        setRecommendation(result);
        setShowResult(true);
      }
    }, 300);
  };
  
  // Navigation
  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } else if (productType) {
      setProductType(null);
      setAnswers({});
    }
  };
  
  // Load clients when modal opens
  const loadClients = async () => {
    setLoadingClients(true);
    try {
      const response = await api.get('/clients');
      setClients(response.data || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error loading clients:', error);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };
  
  // Open client modal instead of navigating directly
  const handleStartFunnel = (strategyId = null, variantId = null) => {
    const strategy = strategyId || recommendation?.strategyId;
    const variant = variantId || recommendation?.variantId;
    
    if (strategy && variant) {
      setPendingNavigation({ strategyId: strategy, variantId: variant });
      setShowClientModal(true);
      loadClients();
    }
  };
  
  // Handle client selection
  const handleSelectClient = (clientId) => {
    setSelectedClientId(clientId);
    setNewClientName(''); // Clear new client input
  };
  
  // Handle new client input
  const handleNewClientInput = (value) => {
    setNewClientName(value);
    setSelectedClientId(null); // Clear selected client
  };
  
  // Confirm and navigate to wizard
  const handleConfirmClient = async () => {
    if (!pendingNavigation) return;
    
    let clientId = selectedClientId;
    
    // Create new client if needed
    if (!clientId && newClientName.trim()) {
      setCreatingClient(true);
      try {
        const response = await api.post('/clients', { name: newClientName.trim() });
        clientId = response.data.id;
        toast.success('Cliente creado correctamente');
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Error creating client:', error);
        toast.error('Error al crear el cliente');
        setCreatingClient(false);
        return;
      }
      setCreatingClient(false);
    }
    
    if (!clientId) {
      toast.error('Selecciona o crea un cliente');
      return;
    }
    
    // Navigate to wizard with all params
    const { strategyId, variantId } = pendingNavigation;
    navigate(`/funnel/new?strategy=${strategyId}&variant=${variantId}&client_id=${clientId}`);
    
    // Reset modal state
    setShowClientModal(false);
    setSelectedClientId(null);
    setNewClientName('');
    setPendingNavigation(null);
  };
  
  // Check if can continue
  const canContinueToWizard = selectedClientId || newClientName.trim();
  
  // Reset
  const handleReset = () => {
    setProductType(null);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowResult(false);
    setRecommendation(null);
  };
  
  // ==================== RENDER ====================
  
  // Product type selection
  if (!productType) {
    return (
      <div className="min-h-screen bg-app-bg flex flex-col">
        {/* Header */}
        <header className="p-6 border-b border-border-subtle">
          <div className="max-w-4xl mx-auto flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand to-purple-600 flex items-center justify-center">
              <Compass className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-text-main">Strategy Advisor</h1>
              <p className="text-sm text-text-muted">Descubre tu funnel ideal</p>
            </div>
          </div>
        </header>
        
        {/* Content */}
        <main className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-2xl w-full">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-text-main mb-3">¿Qué vendes?</h2>
              <p className="text-text-muted">Selecciona el tipo de producto o servicio que ofreces</p>
            </div>
            
            <div className="grid gap-3">
              {Object.values(PRODUCT_TYPES).map((type) => {
                const IconComponent = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => handleProductTypeSelect(type.id)}
                    className="group flex items-center gap-4 p-5 bg-surface rounded-xl border-2 border-transparent hover:border-brand-500 transition-all text-left"
                    data-testid={`product-type-${type.id}`}
                  >
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand/20 to-purple-500/20 flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">
                      {type.emoji}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-text-main text-lg">{type.label}</p>
                      <p className="text-sm text-text-muted">{type.description}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-brand-500 transition-colors" />
                  </button>
                );
              })}
            </div>
          </div>
        </main>
      </div>
    );
  }
  
  // Result screen
  if (showResult && recommendation) {
    const strategy = getStrategyById(recommendation.strategyId);
    const variant = getVariantById(recommendation.strategyId, recommendation.variantId);
    
    // Debug logs (development only)
    if (process.env.NODE_ENV === 'development') {
      console.log('Strategy:', recommendation.strategyId, strategy);
      console.log('Variant:', recommendation.variantId, variant);
    }
    
    // Fallback values
    const strategyLabel = strategy?.label || recommendation.strategyId;
    const strategyIcon = strategy?.icon || '🚀';
    const variantLabel = variant?.label || recommendation.variantId;
    const variantLandings = variant?.defaultAssets?.landings?.length || 3;
    const variantEmails = variant?.defaultAssets?.emails || 5;
    
    return (
      <div className="min-h-screen bg-app-bg flex flex-col">
        {/* Header */}
        <header className="p-6 border-b border-border-subtle">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand to-purple-600 flex items-center justify-center">
                <Compass className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-text-main">Tu recomendación</h1>
                <p className="text-sm text-text-muted">Basada en tus respuestas</p>
              </div>
            </div>
            <Button variant="ghost" onClick={handleReset} className="text-text-muted">
              Empezar de nuevo
            </Button>
          </div>
        </header>
        
        {/* Result */}
        <main className="flex-1 p-6">
          <div className="max-w-3xl mx-auto">
            {/* Main recommendation */}
            <div className="bg-gradient-to-br from-brand/10 to-purple-500/10 rounded-2xl border-2 border-brand-500 p-8 mb-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-brand-500 flex items-center justify-center text-3xl">
                  {strategyIcon}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-brand-500 font-medium mb-1">Estrategia recomendada</p>
                  <h2 className="text-2xl font-bold text-text-main mb-1">{strategyLabel}</h2>
                  <p className="text-lg text-text-secondary">{variantLabel}</p>
                </div>
                <div className="flex items-center gap-1 px-3 py-1.5 bg-[#00D68F]/20 rounded-full">
                  <Sparkles className="w-4 h-4 text-[#00D68F]" />
                  <span className="text-sm font-medium text-[#00D68F]">Match perfecto</span>
                </div>
              </div>
              
              {/* Explanation */}
              <div className="bg-surface/50 rounded-xl p-5 mb-6">
                <p className="text-text-main leading-relaxed">{recommendation.explanation}</p>
              </div>
              
              {/* What you'll get */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { icon: FileText, label: 'Landings', value: variantLandings },
                  { icon: Mail, label: 'Emails', value: variantEmails },
                  { icon: Clock, label: 'Tiempo', value: '2-4 sem' },
                  { icon: Target, label: 'Dificultad', value: 'Media' }
                ].map((stat) => (
                  <div key={stat.label} className="bg-surface rounded-lg p-3 text-center">
                    <stat.icon className="w-5 h-5 text-brand-500 mx-auto mb-1" />
                    <p className="text-lg font-bold text-text-main">{stat.value}</p>
                    <p className="text-xs text-text-muted">{stat.label}</p>
                  </div>
                ))}
              </div>
              
              <Button
                onClick={() => handleStartFunnel()}
                className="w-full bg-brand-500 hover:bg-brand/90 text-white py-6 text-lg font-semibold"
                data-testid="start-funnel-btn"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Crear mi funnel con esta estrategia
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            
            {/* Alternatives */}
            {recommendation.alternatives && recommendation.alternatives.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-text-muted uppercase tracking-wider">
                  Otras opciones para ti
                </h3>
                {recommendation.alternatives.map((alt) => {
                  const altStrategy = getStrategyById(alt.strategyId);
                  return (
                    <button
                      key={`${alt.strategyId}-${alt.variantId}`}
                      onClick={() => handleStartFunnel(alt.strategyId, alt.variantId)}
                      className="w-full flex items-center gap-4 p-4 bg-surface rounded-xl border border-border-subtle hover:border-brand/50 transition-all text-left"
                    >
                      <div className="w-12 h-12 rounded-xl bg-surface-hover flex items-center justify-center text-xl">
                        {altStrategy?.icon || '🎯'}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-text-main">{altStrategy?.label || alt.strategyId}</p>
                        <p className="text-sm text-text-muted">{alt.reason}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-text-muted" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </main>
        
        {/* Client Selection Modal */}
        <Dialog open={showClientModal} onOpenChange={setShowClientModal}>
          <DialogContent className="bg-surface border-border-subtle max-w-md">
            <DialogHeader>
              <DialogTitle className="text-text-main text-xl">
                ¿Para quién es este funnel?
              </DialogTitle>
              <DialogDescription className="text-text-muted">
                Selecciona un cliente existente o crea uno nuevo
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Existing Clients */}
              {loadingClients ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-brand-500" />
                </div>
              ) : clients.length > 0 ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-text-secondary">
                    Clientes existentes
                  </label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {clients.map((client) => (
                      <button
                        key={client.id}
                        onClick={() => handleSelectClient(client.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                          selectedClientId === client.id
                            ? 'border-brand-500 bg-brand/10'
                            : 'border-border-subtle hover:border-brand/50'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          selectedClientId === client.id ? 'bg-brand-500 text-white' : 'bg-surface-hover text-text-muted'
                        }`}>
                          <User className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-text-main">{client.name}</p>
                          {client.funnels_count !== undefined && (
                            <p className="text-xs text-text-muted">{client.funnels_count} funnels</p>
                          )}
                        </div>
                        {selectedClientId === client.id && (
                          <CheckCircle2 className="w-5 h-5 text-brand-500" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
              
              {/* Divider */}
              {clients.length > 0 && (
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-border-subtle" />
                  <span className="text-xs text-text-muted uppercase">o</span>
                  <div className="flex-1 h-px bg-border-subtle" />
                </div>
              )}
              
              {/* New Client */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Crear nuevo cliente
                </label>
                <Input
                  value={newClientName}
                  onChange={(e) => handleNewClientInput(e.target.value)}
                  placeholder="Ej: Academia de Marketing, Juan García..."
                  className="bg-app-bg border-border-subtle text-text-main"
                />
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="ghost"
                onClick={() => setShowClientModal(false)}
                className="flex-1 text-text-muted"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmClient}
                disabled={!canContinueToWizard || creatingClient}
                className="flex-1 bg-brand-500 hover:bg-brand/90 text-white"
              >
                {creatingClient ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creando...
                  </>
                ) : (
                  <>
                    Continuar al wizard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  
  // Questions
  return (
    <div className="min-h-screen bg-app-bg flex flex-col">
      {/* Header */}
      <header className="p-6 border-b border-border-subtle">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-text-muted hover:text-text-main transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Atrás</span>
            </button>
            <span className="text-sm text-text-muted">
              {currentQuestionIndex + 1} de {totalQuestions}
            </span>
          </div>
          
          {/* Progress bar */}
          <div className="h-1.5 bg-surface rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-brand to-purple-600 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </header>
      
      {/* Question */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-2xl w-full">
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">{PRODUCT_TYPES[productType]?.emoji}</span>
              <span className="text-sm text-text-muted uppercase tracking-wider">
                {PRODUCT_TYPES[productType]?.label}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-text-main">
              {currentQuestion?.question}
            </h2>
          </div>
          
          <div className="grid gap-3">
            {currentQuestion?.options.map((option) => {
              const isSelected = answers[currentQuestion.key] === option.id;
              const IconComponent = option.icon;
              
              return (
                <button
                  key={option.id}
                  onClick={() => handleAnswer(option.id)}
                  className={`group flex items-center gap-4 p-5 rounded-xl border-2 transition-all text-left ${
                    isSelected
                      ? 'bg-brand/10 border-brand'
                      : 'bg-surface border-transparent hover:border-brand/50'
                  } ${option.highlight ? 'ring-2 ring-amber-500/30' : ''}`}
                  data-testid={`option-${option.id}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                    isSelected ? 'bg-brand-500 text-white' : 'bg-surface-hover text-text-muted group-hover:text-brand'
                  }`}>
                    {option.emoji ? (
                      <span className="text-xl">{option.emoji}</span>
                    ) : IconComponent ? (
                      <IconComponent className="w-5 h-5" />
                    ) : (
                      <span className="w-3 h-3 rounded-full bg-current" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${isSelected ? 'text-brand' : 'text-text-main'}`}>
                      {option.label}
                    </p>
                    {option.highlight && (
                      <p className="text-xs text-amber-500 mt-0.5">Modo express</p>
                    )}
                  </div>
                  {isSelected && (
                    <CheckCircle2 className="w-5 h-5 text-brand-500" />
                  )}
                </button>
              );
            })}
            
            {/* Custom input for "Otro" options */}
            {currentQuestion?.options.some(o => o.isCustom) && answers[currentQuestion.key] === 'otro' && (
              <div className="mt-3">
                <Input
                  value={customNiche}
                  onChange={(e) => setCustomNiche(e.target.value)}
                  placeholder="Especifica tu nicho..."
                  className="bg-surface border-border-subtle text-text-main"
                />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StrategyAdvisor;
